import type {
  PlainDsdCell,
  PlainDsdRow,
  PlainDsdTable,
} from "@/types/plain-dsd-table";
import type { DsdEditorParsedItem } from "./document-items";

/** 셀 축 라벨: "행라벨 | 열라벨" — 각 경로의 최하위(마지막)를 받는다. 없는 축은 뺀다 */
export const formatDsdAxisLabel = (row: string, column: string) =>
  [row, column].filter(Boolean).join(" | ");

const parseJson = (raw: string, where: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    throw new Error(`${where} 가 JSON 이 아닙니다: ${raw}`);
  }
};

const toLabelPath = (value: unknown, where: string): string[] => {
  if (!Array.isArray(value) || !value.every((v) => typeof v === "string"))
    throw new Error(`${where} 는 문자열 배열이어야 합니다`);
  return value;
};

// 백엔드 렌더러 계약: 행 경로는 <tr data-row-label-path>, 열 경로는 <table data-column-label-paths>[셀 data-grid-col].
// 표에 열 경로 배열이 없으면 표기 경로를 붙이기 전 HTML 이라 표기 없음. 있는데 행·셀 쪽이 빠졌거나 형식이 다르면 계약 위반 throw.
// 열 머리글 행은 <tr data-header-row>(값 무관) — 열 경로를 만든 바로 그 행들이고 표 맨 위부터 이어진다.
// 표시가 하나도 없으면 머리글 정보가 없는 HTML(또는 머리글 없는 표)이라 머리글을 고정하지 않는다.
// 머리글 표시 위반은 문서를 막지 않는다 — 머리글 고정은 부가 기능이라 그 표만 고정을 끄고(행 수 0) Sentry 에 보고한다.

const isHeaderRowMarked = (row: PlainDsdRow) =>
  row.attrs?.["data-header-row"] !== undefined;

const leadingHeaderRowCount = (table: PlainDsdTable) => {
  const firstBody = table.rows.findIndex((row) => !isHeaderRowMarked(row));
  return firstBody === -1 ? table.rows.length : firstBody;
};

/** 머리글 표시 계약 위반 내용. 지키면 null */
export const findPlainDsdHeaderRowViolation = (
  table: PlainDsdTable,
): string | null => {
  const count = leadingHeaderRowCount(table);
  const strayIndex = table.rows.findIndex(
    (row, index) => index >= count && isHeaderRowMarked(row),
  );
  if (strayIndex !== -1)
    return `표 ${table.tableId} 의 data-header-row 가 맨 위부터 이어지지 않습니다 (${strayIndex}행)`;
  if (count > 0 && table.attrs["data-column-label-paths"] === undefined)
    return `표 ${table.tableId} 에 data-header-row 가 있는데 data-column-label-paths 가 없습니다`;
  return null;
};

/** 표 맨 위의 열 머리글 행 수 — 고정할 행. 표시가 없거나 계약 위반이면 0 */
export const getPlainDsdHeaderRowCount = (table: PlainDsdTable) =>
  findPlainDsdHeaderRowViolation(table) ? 0 : leadingHeaderRowCount(table);

const readColumnLabelPaths = (table: PlainDsdTable): string[][] | null => {
  const raw = table.attrs["data-column-label-paths"];
  if (raw === undefined) return null;
  const where = `표 ${table.tableId} 의 data-column-label-paths`;
  const parsed = parseJson(raw, where);
  if (!Array.isArray(parsed)) throw new Error(`${where} 는 배열이어야 합니다`);
  return parsed.map((path, index) => toLabelPath(path, `${where}[${index}]`));
};

const readRowLabelPath = (table: PlainDsdTable, row: PlainDsdRow) => {
  const where = `표 ${table.tableId} 의 data-row-label-path`;
  const raw = row.attrs?.["data-row-label-path"];
  if (raw === undefined) throw new Error(`${where} 가 행에 없습니다`);
  return toLabelPath(parseJson(raw, where), where);
};

const readGridCol = (cell: PlainDsdCell, columnCount: number) => {
  const raw = cell.attrs["data-grid-col"];
  if (raw === undefined)
    throw new Error(`셀 ${cell.cellId} 에 data-grid-col 이 없습니다`);
  const gridCol = Number(raw);
  if (!Number.isInteger(gridCol) || gridCol < 0 || gridCol >= columnCount)
    throw new Error(
      `셀 ${cell.cellId} 의 data-grid-col(${raw}) 이 열 경로 개수(${columnCount}) 범위 밖입니다`,
    );
  return gridCol;
};

/** 표 전체의 표기 경로 계약 검사 — 데이터를 불러올 때 한 번. 위반이면 throw */
export const assertPlainDsdTableLabelPaths = (table: PlainDsdTable) => {
  const columns = readColumnLabelPaths(table);
  if (!columns) return;
  for (const row of table.rows) {
    readRowLabelPath(table, row);
    for (const cell of row.cells) readGridCol(cell, columns.length);
  }
};

/** 셀의 행·열 표기 경로. 표기 경로가 없는 HTML 이면 null, 계약 위반이면 throw */
export const getPlainDsdCellLabelPaths = (
  table: PlainDsdTable,
  row: PlainDsdRow,
  cell: PlainDsdCell,
): { row: string[]; column: string[] } | null => {
  const columns = readColumnLabelPaths(table);
  if (!columns) return null;
  return {
    row: readRowLabelPath(table, row),
    column: columns[readGridCol(cell, columns.length)],
  };
};

/**
 * 문서에서 셀을 찾아 축 라벨을 만든다. 셀이 없거나 표기 경로가 없는 HTML 이면 "".
 * 리뷰 데이터 훅이 불러올 때 assertPlainDsdTableLabelPaths 로 검사하므로 화면에서는 throw 하지 않는다
 */
export const getDsdCellAxisLabel = (
  items: DsdEditorParsedItem[],
  cellId: string,
): string => {
  for (const item of items) {
    if (item.kind !== "table") continue;
    for (const row of item.table.rows) {
      const cell = row.cells.find((candidate) => candidate.cellId === cellId);
      if (!cell) continue;
      const paths = getPlainDsdCellLabelPaths(item.table, row, cell);
      return paths
        ? formatDsdAxisLabel(paths.row.at(-1) ?? "", paths.column.at(-1) ?? "")
        : "";
    }
  }
  return "";
};
